import { type NonNullableProperties } from '../../utils/types/non-nullable-properties';
import { type Address } from '../address/address';
import { type CustomKeyValue } from '../custom-key-value';
import { type UploadedDocument } from '../uploaded-document';
import { type TokenKey } from '../user/token-key';

import { type BankAccount } from './bank-account';
import { type BusinessOwner, type BusinessOwnerCreate } from './business-owner';
import { type CompanyContact } from './company-contact';
import { type MerchantCategoryCode } from './merchant-category-code';

/** Company. */
export type Company = Readonly<{

	/** ID. */
	id: number;

	/** Name. */
	name: string;

	/** Doing business as. */
	dba: string;

	/** Employer identification number. */
	ein: string;

	/** Merchant Category Code. */
	merchantCategoryCode: MerchantCategoryCode | null;

	/** When company opened. */
	openDate: Date | null;

	/** Refund policy. */
	refundPolicy: CustomKeyValue<string, string>;

	/** Business service. */
	businessService: string;

	/** Email. */
	email: string;

	/** Phone. */
	phone: string;

	/** Fax. */
	fax: string | null;

	/** URL. */
	url: string | null;

	/** Business address. */
	address: Address;

	/** Annual CC sales. */
	annualCCSales: number;

	/** Average ticket amount. */
	ticketAverage: number;

	/** Bank account information. */
	bankAccount: BankAccount;

	/** Business owner for contact. */
	contactInfo: CompanyContact | null;

	/** Deactivated date. */
	deactivatedAt: Date | null;

	/** Seasonal status. */
	seasonalStatus: boolean;

	/** Company type. */
	companyType: CustomKeyValue<string, string>;

	/** Owner first name. */
	ownerFirstName: string;

	/** Owner last name. */
	ownerLastName: string;

	/** Business owner. */
	businessOwners: readonly BusinessOwner[];

	/** Merchant status. */
	merchantStatus: string;

	/** Is company public. */
	isPublic: boolean;

	/** Is company registered in payrix. */
	isRegisteredInPayrix: boolean;

}>;

/**
 * Namespace to work with company entities.
 * Contains util methods for easier model use.
 */
export namespace Company {

	/**
	 * Whether the company is editable or not.
	 * Company is editable if this one has approved merchant status.
	 * @param data Company.
	 */
	export function isEditable(data: Company): boolean {
		return data.merchantStatus !== '';
	}

	/**
	 * Whether the company is verified by payment provider or not.
	 * Company is verified if this one has ready/boarded merchant status.
	 * @param data Company.
	 */
	export function isVerified(data: Company): boolean {
		return (
			data.merchantStatus === 'Ready' ||
			data.merchantStatus === 'Boarded'
		);
	}
}

/** Data for the creation company. */
export type CompanyCreate = Pick<
	Company,
	| 'name'
	| 'ownerFirstName'
	| 'ownerLastName'
	| 'email'
	| 'phone'
	| 'address'
>;

/** Data for prefilling second step registration form. */
export type CompanySecondStepPrefill = Omit<CompanyCreate, | 'name'> & Readonly<{

	/** Token, get from back and needs to register company. */
	token: TokenKey;
}>;

/** Data for the creation company by second step of registration process. */
export type CompanySecondStepCreate = NonNullableProperties<Pick<
	Company,
	| 'dba'
	| 'ein'
	| 'openDate'
	| 'refundPolicy'
	| 'businessService'
	| 'url'
	| 'ticketAverage'
	| 'annualCCSales'
	| 'bankAccount'
	| 'seasonalStatus'
	| 'companyType'
	| 'merchantCategoryCode'
	| 'fax'
	| 'isPublic'
>> & Readonly<{

	/** Void check. */
	voidCheck: readonly File[] | readonly UploadedDocument[];

	/** Business owners. */
	businessOwners: readonly BusinessOwnerCreate[];
}>;

/** Data to update company. */
export type CompanyUpdate = CompanyCreate & Omit<CompanySecondStepCreate, 'voidCheck' | 'businessOwners'> & Readonly<{

	/** Business owners. */
	businessOwners: readonly BusinessOwner[];
}>;
